import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Chip,
  Stack,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Tooltip,
  CircularProgress,
  ToggleButtonGroup,
  ToggleButton,
  Select,
  MenuItem,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { debounce } from 'lodash';
import { useNavigate } from 'react-router-dom';
import wineNameService, { WineName } from '../../../services/wineNameService';
import ImportExportModal from '../../../components/ImportExportModal';
import { wineNameConfig } from '../../../utils/importExportConfigs';

const WineNameList: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();

  const [loading, setLoading] = useState(true);
  const [wineNames, setWineNames] = useState<WineName[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [reviewFilter, setReviewFilter] = useState<'all' | 'needs_review' | 'complete'>('all');
  const [importExportOpen, setImportExportOpen] = useState(false);

  const [pagination, setPagination] = useState({
    page: 0,
    pageSize: 25,
    totalItems: 0,
    totalPages: 0,
  });

  const debouncedSearch = useMemo(
    () => debounce((query: string) => setSearchQuery(query), 300),
    []
  );

  const fetchWineNames = useCallback(async () => {
    setLoading(true);
    try {
      const params: Record<string, any> = {
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
      };
      if (searchQuery.trim()) params.search = searchQuery.trim();
      if (activeFilter === 'active') params.active = true;
      if (activeFilter === 'inactive') params.active = false;

      const response = await wineNameService.getAll(params);
      setWineNames(response.data);
      setPagination((prev) => ({
        ...prev,
        totalItems: response.pagination.totalItems,
        totalPages: response.pagination.totalPages,
      }));
    } catch (error) {
      console.error('Failed to fetch wine names:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.pageSize, searchQuery, activeFilter]);

  useEffect(() => {
    fetchWineNames();
  }, [fetchWineNames]);

  const handleDelete = useCallback(
    async (id: string) => {
      const wineName = wineNames.find((w) => w._id === id);
      if (!wineName) return;
      if (window.confirm(`Delete "${wineName.displayName}"?`)) {
        try {
          await wineNameService.delete(id);
          fetchWineNames();
        } catch (error) {
          console.error('Failed to delete wine name:', error);
          alert('Failed to delete. Please try again.');
        }
      }
    },
    [wineNames, fetchWineNames]
  );

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedSearch(e.target.value);
    },
    [debouncedSearch]
  );

  const handleChangePage = useCallback(
    (_: unknown, newPage: number) => {
      setPagination((prev) => ({ ...prev, page: newPage }));
    },
    []
  );

  const handleChangeRowsPerPage = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setPagination((prev) => ({
        ...prev,
        pageSize: parseInt(e.target.value, 10),
        page: 0,
      }));
    },
    []
  );

  const hasNeedsReview = (wn: WineName): boolean =>
    !!(
      wn.producer?.needsReview ||
      wn.region?.needsReview ||
      wn.labelGrapes?.some((g) => g.needsReview)
    );

  const visibleWineNames = useMemo(() => {
    if (reviewFilter === 'needs_review') return wineNames.filter(hasNeedsReview);
    if (reviewFilter === 'complete') return wineNames.filter((wn) => !hasNeedsReview(wn));
    return wineNames;
  }, [wineNames, reviewFilter]);

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            gap: 2,
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            flexWrap: 'wrap',
          }}
        >
          <TextField
            placeholder="Search wine names..."
            variant="outlined"
            size="small"
            onChange={handleSearchChange}
            sx={{
              flexGrow: 1,
              maxWidth: { xs: '100%', sm: 360 },
              '& .MuiOutlinedInput-root': {
                backgroundColor: theme.palette.background.paper,
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                </InputAdornment>
              ),
            }}
          />

          <ToggleButtonGroup
            size="small"
            exclusive
            value={activeFilter}
            onChange={(_, val) => val && setActiveFilter(val)}
          >
            <ToggleButton value="all">All</ToggleButton>
            <ToggleButton value="active">Active</ToggleButton>
            <ToggleButton value="inactive">Inactive</ToggleButton>
          </ToggleButtonGroup>

          <Select
            value={reviewFilter}
            onChange={(e) => setReviewFilter(e.target.value as typeof reviewFilter)}
            sx={{ minWidth: 130, '& .MuiSelect-select': { py: '6.5px', fontSize: '0.875rem' } }}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="needs_review">Needs Review</MenuItem>
            <MenuItem value="complete">Complete</MenuItem>
          </Select>

          <Button
            variant="outlined"
            onClick={() => setImportExportOpen(true)}
            sx={{ minWidth: 'auto', px: 2 }}
          >
            Import / Export
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/admin/wine-names/create')}
            sx={{ minWidth: 'auto', px: 3 }}
          >
            Add Wine Name
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : visibleWineNames.length === 0 ? (
          <Typography variant="body1" align="center" sx={{ p: 3 }}>
            No wine names found. Click "Add Wine Name" to create one.
          </Typography>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Display Name</TableCell>
                    <TableCell>Producer</TableCell>
                    <TableCell>Region</TableCell>
                    <TableCell>Label Grapes</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleWineNames.map((wn) => (
                    <TableRow
                      key={wn._id}
                      hover
                      onClick={() => navigate(`/admin/wine-names/edit/${wn._id}`)}
                      sx={{
                        cursor: 'pointer',
                        '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {wn.displayName}
                          </Typography>
                          {hasNeedsReview(wn) && (
                            <Tooltip title="Some linked records need review">
                              <WarningIcon
                                fontSize="small"
                                sx={{ color: theme.palette.warning.main }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                        {wn.specialName && (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            {wn.specialName}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <Typography variant="body2">
                            {wn.producer?.fullName || wn.producer?.name}
                          </Typography>
                          {wn.producer?.needsReview && (
                            <Tooltip title="Needs review">
                              <WarningIcon
                                fontSize="small"
                                sx={{ color: theme.palette.warning.main, fontSize: 14 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>

                      <TableCell>
                        {wn.region ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography variant="body2">{wn.region.name}</Typography>
                            {wn.region.needsReview && (
                              <Tooltip title="Needs review">
                                <WarningIcon
                                  fontSize="small"
                                  sx={{ color: theme.palette.warning.main, fontSize: 14 }}
                                />
                              </Tooltip>
                            )}
                          </Box>
                        ) : (
                          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        {wn.labelGrapes && wn.labelGrapes.length > 0 ? (
                          <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
                            {wn.labelGrapes.map((g) => (
                              <Chip
                                key={g._id}
                                label={g.primaryName}
                                size="small"
                                icon={
                                  g.needsReview ? (
                                    <WarningIcon sx={{ fontSize: '12px !important' }} />
                                  ) : undefined
                                }
                                sx={{
                                  backgroundColor: alpha(theme.palette.secondary.main, 0.12),
                                  color: theme.palette.secondary.main,
                                  fontWeight: 500,
                                }}
                              />
                            ))}
                          </Stack>
                        ) : (
                          <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
                            —
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Chip
                          label={wn.active ? 'Active' : 'Inactive'}
                          size="small"
                          sx={{
                            backgroundColor: wn.active
                              ? alpha(theme.palette.success.main, 0.12)
                              : alpha(theme.palette.text.disabled, 0.12),
                            color: wn.active
                              ? theme.palette.success.main
                              : theme.palette.text.disabled,
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>

                      <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                        <Tooltip title="Edit">
                          <IconButton
                            color="primary"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/admin/wine-names/edit/${wn._id}`);
                            }}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            color="error"
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDelete(wn._id);
                            }}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={pagination.totalItems}
              page={pagination.page}
              onPageChange={handleChangePage}
              rowsPerPage={pagination.pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>
      <ImportExportModal
        open={importExportOpen}
        onClose={() => { setImportExportOpen(false); fetchWineNames(); }}
        config={wineNameConfig}
      />
    </Box>
  );
};

export default WineNameList;
